import { memo } from "react";
import { useRecoilValue } from "recoil";
import { Form, Formik } from "formik";
import { Box } from "@mui/material";

import { FormBuilder } from "../../../Configuration/FormBuilder";
import {
  ApiResultsType,
  ConfigurationFormType,
  FormDetailsType,
  SelectorAreaType,
} from "../../../Configuration/interfaces";
import {
  createInitialValues,
  getSubmitValue,
  useYupSchema,
} from "../../../Configuration/EditForm";
import {
  createUrlParams,
  mergeFormOptions,
} from "../../../Configuration/utils";
import { GenericObject } from "../../../services/Price";
import { currentUserAtom } from "../../../AuthProvider";

const fieldsOptions: FormDetailsType = [
  {
    key: "analysis",
    secondaryKey: "block_name",
    label: "BLOC_MAIL",
    multiple: true,
    controlledInputSize: true,
    type: ConfigurationFormType.StaticSelect,
  },
  {
    key: "granularity",
    secondaryKey: "granularity",
    label: "ZONE",
    type: ConfigurationFormType.StaticSelect,
    watchedBy: "perimeters",
    interpretNullAsAll: true,
  },
  {
    key: "perimeters",
    label: "SPATIAL_PERIMETER",
    type: ConfigurationFormType.MultiSelect,
    multiple: true,
    controlledInputSize: true,
    interpretNullAsAll: true,
    // Needed when "type" is "ConfigurationFormType.MultiSelect", used to load multi api in single select according other field input value
    multiApi: {
      watchFilter: "granularity",
      apisFieldMap: {
        ALL: null,
        PROPERTY: "property",
        ZONE_GEO_1: "geozone1",
        ZONE_GEO_2: "geozone2",
      },
      apis: [
        {
          api: "",
          caseOfValueIs: SelectorAreaType.ALL,
          labelKey: "",
          defaultValue: "ALL",
        },
        {
          api: "/api/properties/",
          apiParams: {
            page_size: 100,
            ordering: "hebergement_nom_court",
          },
          caseOfValueIs: SelectorAreaType.PROPERTY,
          labelKey: "hebergement_nom_court",
        },
        {
          api: "/api/property-geo-zones-1/",
          apiParams: {
            page_size: 100,
            ordering: "short_name",
          },
          caseOfValueIs: SelectorAreaType.GEO_AREA_1,
          labelKey: "short_name",
        },
        {
          api: "/api/property-geo-zones-2/",
          apiParams: {
            page_size: 100,
            ordering: "short_name",
          },
          caseOfValueIs: SelectorAreaType.GEO_AREA_2,
          labelKey: "short_name",
        },
      ],
    },
  },
  {
    key: "language",
    label: "LANGUAGE",
    type: ConfigurationFormType.Select,
    api: "/api/user/bloc-mails/languages/",
    apiParams: {
      page_size: 100,
      ordering: "short_name",
    },
    apiResultsType: ApiResultsType.Array,
    labelKey: "values",
  },
  {
    key: "date_range",
    startDateField: "start",
    endDateField: "end",
    startDateLabel: "TIME_PERIMETER_BEGIN",
    endDateLabel: "TIME_PERIMETER_END",
    type: ConfigurationFormType.DateRangePicker,
  },
];

const editOptions = [
  {
    key: "analysis",
    flex: "45%",
    required: true,
  },
  {
    key: "language",
    flex: "45%",
    required: true,
  },
  {
    key: "granularity",
    flex: "45%",
    required: true,
  },
  {
    key: "perimeters",
    flex: "45%",
    required: true,
  },
  {
    key: "date_range",
    flex: "100%",
    required: true,
  },
];

const createOptions = [
  {
    key: "analysis",
    flex: "45%",
    required: true,
  },
  {
    key: "language",
    flex: "45%",
    required: true,
  },
  {
    key: "granularity",
    flex: "45%",
    required: true,
  },
  {
    key: "perimeters",
    flex: "45%",
    required: true,
  },
  {
    key: "date_range",
    flex: "100%",
    required: true,
  },
];

const MailsFormTemplate = memo(
  ({
    mailData,
    onSubmit,
    onUpdate,
  }: {
    mailData?: GenericObject;
    onSubmit?: ({
      url,
      data,
      options,
    }: {
      url: string;
      data: GenericObject;
      options: FormDetailsType;
    }) => void;
    onUpdate?: ({
      mailId,
      url,
      data,
    }: {
      mailId: number;
      url: string;
      data: any;
    }) => void;
  }) => {
    const currentUser = useRecoilValue(currentUserAtom);

    const yupSchema = useYupSchema(
      mergeFormOptions(createOptions, fieldsOptions)
    );

    const initialValues = createInitialValues(
      fieldsOptions,
      null,
      undefined,
      mailData
    );

    const handleSubmitForm = (formData: GenericObject) => {
      if (currentUser) {
        const url = mailData
          ? createUrlParams(`/api/user/bloc-mails/${mailData.id}`)
          : createUrlParams("/api/user/bloc-mails");

        const data = getSubmitValue(
          mergeFormOptions(createOptions, fieldsOptions),
          formData
        );

        if (mailData && onUpdate) {
          onUpdate({ mailId: mailData.id, url, data });
        } else if (onSubmit) {
          onSubmit({
            url,
            data,
            options: mergeFormOptions(createOptions, fieldsOptions),
          });
        }
      }
    };

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmitForm(values);
          setSubmitting(false);
        }}
        validationSchema={yupSchema}
        validateOnChange
      >
        {() => (
          <Form
            id={
              mailData
                ? `notification-mails-form-${mailData.id}`
                : "notification-mails-form"
            }
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormBuilder
                fieldsOptions={mergeFormOptions(editOptions, fieldsOptions)}
                row={mailData}
                contrastedStyles
                dynamicPath={true}
              />
            </Box>
          </Form>
        )}
      </Formik>
    );
  }
);

export default MailsFormTemplate;
